import React from "react";
import { Link } from "react-router-dom";
import BrandLogo from "../../components/common/BrandLogo";

interface PublicInfoLayoutProps {
  title: string;
  subtitle: string;
  updatedAt: string;
  canonicalPath?: string;
  structuredData?: Record<string, unknown> | Array<Record<string, unknown>>;
  children: React.ReactNode;
}

const freePlanItems = [
  "Primeros 30 días con funciones Pro completas",
  "Hasta 1 cuenta bancaria",
  "Hasta 1 tarjeta",
  "50 movimientos procesados al mes",
  "Hasta 3 reglas automáticas",
  "Histórico de 3 meses",
];

const proPlanItems = [
  "Cuentas y tarjetas sin límite",
  "Importaciones y altas manuales sin límite",
  "Reglas automáticas ilimitadas",
  "Histórico completo y análisis avanzado",
  "Plan de ahorro completo",
];

export default function PublicInfoLayout({
  title,
  subtitle,
  updatedAt,
  canonicalPath,
  structuredData,
  children,
}: PublicInfoLayoutProps) {
  React.useEffect(() => {
    const previousTitle = document.title;
    const descriptionNode = document.querySelector('meta[name="description"]');
    const previousDescription = descriptionNode?.getAttribute("content") ?? "";
    const canonicalNode = document.querySelector('link[rel="canonical"]');
    const previousCanonical = canonicalNode?.getAttribute("href") ?? "";

    document.title = `${title} | Ahorra y respira`;
    if (descriptionNode) {
      descriptionNode.setAttribute("content", subtitle);
    }
    if (canonicalNode && canonicalPath) {
      canonicalNode.setAttribute("href", `https://ahorrayrespira.com${canonicalPath}`);
    }

    const payload = Array.isArray(structuredData) ? structuredData : structuredData ? [structuredData] : [];
    payload.forEach((item, index) => {
      const node = document.createElement("script");
      node.type = "application/ld+json";
      node.id = `ld-public-${index}`;
      node.text = JSON.stringify(item);
      document.head.appendChild(node);
    });

    return () => {
      document.title = previousTitle;
      if (descriptionNode) {
        descriptionNode.setAttribute("content", previousDescription);
      }
      if (canonicalNode && previousCanonical) {
        canonicalNode.setAttribute("href", previousCanonical);
      }
      payload.forEach((_, index) => {
        document.getElementById(`ld-public-${index}`)?.remove();
      });
    };
  }, [title, subtitle, canonicalPath, structuredData]);

  return (
    <div className="landing-shell">
      <header className="navbar">
        <div className="navbar-inner navbar-inner--landing">
          <Link to="/" className="brand" aria-label="Ahorra y respira">
            <BrandLogo size={30} />
          </Link>
          <nav className="nav-actions">
            <Link to="/" className="btn btn-secondary">Inicio</Link>
            <Link to="/auth/register" className="btn btn-primary btn-nav-signup">Crea tu cuenta gratis</Link>
          </nav>
        </div>
      </header>

      <main className="page-section">
        <div className="section-content public-info-page">
          <p className="tag">Guía práctica</p>
          <h1 className="section-title" style={{ marginTop: "1rem" }}>{title}</h1>
          <p className="section-subtitle">{subtitle}</p>
          <p className="legal-updated">Última actualización: {updatedAt}</p>

          {children}

          <section className="panel public-info-links">
            <h2 className="panel-title">Siguiente paso</h2>
            <p>Empiezas gratis con 30 días completos. Si quieres mantener todas las funciones después, pasas a Pro por 2,99 €/mes.</p>
            <div className="public-next-grid">
              <article className="landing-pricing-card public-next-card">
                <p className="landing-pricing-name">Free</p>
                <p className="landing-pricing-price">0 €</p>
                <p className="landing-pricing-period">siempre</p>
                <p className="landing-pricing-description">Incluye 30 días de prueba completa y después sigue gratis con límites.</p>
                <ul className="landing-list">
                  {freePlanItems.map(item => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
                <Link to="/auth/register" className="btn btn-secondary">Empezar gratis</Link>
              </article>
              <article className="landing-pricing-card is-pro public-next-card">
                <p className="landing-pricing-name">Pro</p>
                <p className="landing-pricing-price">2,99 €</p>
                <p className="landing-pricing-period">al mes · 29 € al año</p>
                <p className="landing-pricing-description">Para trabajar sin límites y con histórico completo.</p>
                <ul className="landing-list">
                  {proPlanItems.map(item => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
                <Link to="/auth/register" className="btn btn-primary">Contratar Pro</Link>
              </article>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
}
